/**
 * Feature-based plan tiers (baseball and softball). Mirrors `api/RosterPilotAPI/DTOs/PlanTierDtos.cs`.
 *
 * A plan feature is something a plan can include or leave out. Everything else (players, notes,
 * calls, schedule, roster planning) is the core app every program gets. Which features each plan
 * includes is set by system admins, so nothing here assumes a plan's contents.
 */

/** The plan feature keys the API knows. A client contract: never renamed. */
export const PLAN_FEATURES = {
  playerDatabase: 'player_database',
  transferPortal: 'transfer_portal',
  rankings: 'rankings',
  textMessaging: 'text_messaging',
  powerFourPortalPlayers: 'p4_portal_players',
  nilTracking: 'nil_tracking',
  playerValuations: 'player_valuations',
  portalProjections: 'portal_projections',
} as const;

/**
 * Features the API refuses without another one (400 `invalid_features`): key -> the key it needs.
 * Used only for the admin hint; the server is the rule.
 */
export const PLAN_FEATURE_REQUIRES: Readonly<Record<string, string>> = {
  [PLAN_FEATURES.powerFourPortalPlayers]: PLAN_FEATURES.transferPortal,
  [PLAN_FEATURES.rankings]: PLAN_FEATURES.playerDatabase,
  [PLAN_FEATURES.playerValuations]: PLAN_FEATURES.rankings,
  [PLAN_FEATURES.portalProjections]: PLAN_FEATURES.transferPortal,
};

export type PlanFeatureKey = (typeof PLAN_FEATURES)[keyof typeof PLAN_FEATURES];

/** One plan feature as a coach reads it. */
export interface PlanFeature {
  key: string;
  name: string;
  description: string;
}

/** A plan on sale, as signup lookup and the public pricing page list it. */
export interface PlanTierOffer {
  /** Immutable key; signup sends it back as `planTier`. */
  key: string;
  name: string;
  description: string | null;
  sport: 'baseball' | 'softball';
  annualPriceCents: number;
  /**
   * The yearly price with a 3-year agreement (billed yearly). Null or absent (an older API) means
   * the 3-year option is not offered.
   */
  threeYearAnnualPriceCents?: number | null;
  /** What this plan adds to the core app, in registry order. Empty for a core-only plan. */
  features: PlanFeature[];
  sortOrder: number;
}

/** GET api/admin/self-serve/plan-tiers, one row. */
export interface PlanTierAdmin {
  key: string;
  sport: string;
  name: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  annualPriceCents: number;
  currency: string;
  /** Included plan feature keys, in registry order. */
  features: string[];
  stripePriceId: string | null;
  stripePriceCurrent: boolean;
  /** Active, priced and Stripe on: a coach can pick it at signup now. */
  purchasable: boolean;
  /** Live organizations on this plan. */
  organizationCount: number;
  updatedAt: string;
}

/** GET api/admin/self-serve/plan-tiers */
export interface PlanTiersAdminResponse {
  stripeEnabled: boolean;
  /** Every feature a plan can include (the checkboxes). */
  features: PlanFeature[];
  /** Sports plans can be made for (baseball and softball). */
  sports: string[];
  tiers: PlanTierAdmin[];
}

/**
 * POST api/admin/self-serve/plan-tiers and PUT .../plan-tiers/{key}. `features` is required (an
 * empty list is a core-only plan). `sport` is read on create only. Price 0 means not sold, else
 * 100..10,000,000 cents. Refusals are `{ code, message }`: 400 invalid_name | invalid_description |
 * invalid_sort_order | invalid_price | invalid_features | invalid_sport, 404, 409 name_taken,
 * 502 payment_provider_error.
 */
export interface SavePlanTierRequest {
  name: string;
  description?: string | null;
  sport?: string;
  isActive?: boolean;
  sortOrder?: number;
  annualPriceCents: number;
  features: string[];
}

/**
 * PUT api/admin/organizations/{id}/plan-tier. Null takes the program off plan tiers (every feature).
 * `extraFeatures` are features on top of the plan; omitted keeps the stored extras.
 */
export interface SetOrganizationPlanTierRequest {
  planTierKey: string | null;
  extraFeatures?: string[];
}

export interface OrganizationPlanTierResponse {
  organizationId: string;
  planTierKey: string | null;
  planTierName: string | null;
  /** The plan's features plus the extras. */
  planFeatures: string[];
  /** Features on top of the plan. Absent from an API older than extras. */
  extraPlanFeatures?: string[];
}

/**
 * Whether the signed-in program's plan includes a feature. `planFeatures` absent or null (an API
 * older than plan tiers) means every feature: hiding on a missing field would take features away
 * from every paying program during a deploy.
 */
export const hasPlanFeature = (
  planFeatures: readonly string[] | null | undefined,
  key: PlanFeatureKey,
): boolean => planFeatures == null || planFeatures.includes(key);
